import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useAppSelector } from "../hooks/redux";
import { authSelector } from "../store/slices/authSlice";
import SignInToContinueModal from "../components/feature/SignInToContinueModal/SignInToContinueModal";

type AuthPromptContextValue = {
  isAuthenticated: boolean;
  /** Returns true if authenticated; otherwise opens the sign-in modal and returns false. */
  requireAuth: () => boolean;
  promptSignIn: () => void;
};

const AuthPromptContext = createContext<AuthPromptContextValue | null>(null);

export function AuthPromptProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const authState = useAppSelector(authSelector) as { token?: string | null };
  const isAuthenticated = Boolean(authState?.token);
  const [visible, setVisible] = useState(false);

  const promptSignIn = useCallback(() => {
    setVisible(true);
  }, []);

  const requireAuth = useCallback(() => {
    if (isAuthenticated) {
      return true;
    }
    setVisible(true);
    return false;
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      requireAuth,
      promptSignIn,
    }),
    [isAuthenticated, requireAuth, promptSignIn]
  );

  return (
    <AuthPromptContext.Provider value={value}>
      {children}
      <SignInToContinueModal
        visible={visible}
        onClose={() => setVisible(false)}
      />
    </AuthPromptContext.Provider>
  );
}

export function useRequireAuth(): AuthPromptContextValue {
  const context = useContext(AuthPromptContext);
  if (!context) {
    throw new Error("useRequireAuth must be used within AuthPromptProvider");
  }
  return context;
}
